import type { ICampaignGroup } from '~/modules/campaign-group/types';

import {
  useCampaignNavigate,
  getRoutesPathByCampaign,
  DISABLE_STATE_BY_STATUS,
} from '~/modules/campaign';
import { useGModalDelete, useGModalRename, GCGTemplateActionByState } from '~/presentation/common';
import { ACTION_PRESET, GActionList, GButton, GStopPropagation } from '~/presentation/ui';
import { useCGTemplateEnded } from '~/modules/campaign-group-template/hooks';
import { IDisableStateByStatus } from '~/modules/campaign/types';
import { useHandleNavigate } from '~/hooks/useHandleNavigate';
import { ChartVerticalIcon } from '@shopify/polaris-icons';
import { CAMPAIGN_STATUS } from '~/constants/campaign';
import { InlineStack } from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

interface CGTemplateActionProps {
  campaignGroup: ICampaignGroup;
  mobileOrder?: boolean;
}

export const CGTemplateAction: React.FC<CGTemplateActionProps> = ({
  campaignGroup,
  mobileOrder,
}) => {
  const { t } = useTranslation();
  const { onEndCampaign } = useCGTemplateEnded();
  const { handleNavigate } = useHandleNavigate();
  const { handleOpenRenameModal } = useGModalRename();
  const { handleOpenDeleteModal } = useGModalDelete();
  const { onAnalyticsCampaign } = useCampaignNavigate();

  const pathDetail = useMemo(() => getRoutesPathByCampaign(campaignGroup!), [campaignGroup]);

  const isCampaignDraft = useMemo(
    () => campaignGroup?.status === CAMPAIGN_STATUS.DRAFT,
    [campaignGroup],
  );
  const isCampaignActive = useMemo(
    () => campaignGroup?.status === CAMPAIGN_STATUS.RUNNING,
    [campaignGroup],
  );
  const isDisabledEnded = useMemo(() => {
    if (!campaignGroup.status) return true;

    return DISABLE_STATE_BY_STATUS[IDisableStateByStatus.ACTION_END].includes(campaignGroup.status);
  }, [campaignGroup]);

  const handleOpenAnalytic = () => {
    if (isCampaignDraft) return;

    onAnalyticsCampaign(campaignGroup!);
  };

  const stateButton = <GCGTemplateActionByState campaignGroup={campaignGroup!} />;
  const analyticsButton = (
    <GButton
      icon={ChartVerticalIcon}
      disabled={isCampaignDraft}
      onClick={handleOpenAnalytic}
    ></GButton>
  );
  const moreButton = (
    <GActionList
      height={mobileOrder ? 'auto' : undefined}
      preferredPosition={mobileOrder ? 'above' : undefined}
      preferredAlignment={mobileOrder ? 'right' : undefined}
      actionList={
        {
          items: [
            {
              ...ACTION_PRESET.EDIT,
              onAction: () => handleNavigate(pathDetail),
            },
            {
              ...ACTION_PRESET.RENAME,
              onAction: () =>
                handleOpenRenameModal({
                  name: campaignGroup.title ?? '',
                  id: campaignGroup.id,
                }),
            },
            // {
            //   ...ACTION_PRESET.COPY,
            //   content: 'Copy link',
            //   onAction: () => handleCopyLink(),
            // },
            // {
            //   ...ACTION_PRESET.VIEW_LIVE,
            //   content: 'Preview in browser',
            // },
            {
              ...ACTION_PRESET.ENDED,
              disabled: isDisabledEnded,
              onAction: () => onEndCampaign(campaignGroup),
            },
            {
              ...ACTION_PRESET.DELETE,
              ...(isCampaignActive
                ? {
                    disabled: true,
                    helpText: t('Live experiment cannot be deleted'),
                  }
                : {}),
              onAction: () =>
                handleOpenDeleteModal({
                  name: campaignGroup.title ?? '',
                  id: campaignGroup.id,
                }),
            },
          ],
        } as any
      }
    />
  );
  return (
    <GStopPropagation>
      <InlineStack gap="200" wrap={false}>
        {mobileOrder ? (
          <>
            {moreButton}
            {stateButton}
            {analyticsButton}
          </>
        ) : (
          <>
            {stateButton}
            {analyticsButton}
            {moreButton}
          </>
        )}
      </InlineStack>
    </GStopPropagation>
  );
};
